import { useCallback, useState } from 'react';
import {
  ApiCheckUnlock,
  ApiCheckUnlockPay,
  fetcher, isApiV1Errors, isCheckUnlockStatusOk, isCheckUnlockStatusPayOk,
  ResponseWithHeaders,
} from '@utils/data-fetching';
import { bjToast } from '@components/common';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import { showCvContactModal } from '@root/src/components/features/modals/employer/CvContactModal';
import {
  showUpgradeSubscriptionModal,
} from '@components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import {
  showAccountSuspendedModal,
} from '@components/features/modals/employer/subscription/AccountSuspendedModal';
import { showAddCompanyInfoModal } from '@root/src/components/features/modals/employer/AddCompanyInfoModal';
import { trackEvent } from '@services/tracking';
import {
  showAccountFirstPaymentModal,
} from '@components/features/modals/employer/subscription/AccountFirstPaymentModal';

interface IProps {
  userIdToUnlock?: number;
  jobSlug?: string;
  jobTitle?: string;
  jobFolderId?: number;
  rid?: string;
  isCompany?: boolean;
  userId?: number;
  slug?: string;
  toFetchOperationJob?: boolean;
  locale?: Locale;
}

interface UnlockOptions {
  type: 'download' | 'message' | 'contact';
  onSuccess?: (response: ApiCheckUnlock) => void;
}
/**
 *
 * @param userIdToUnlock - the slug of the cv
 * @param rid - query param. can be used in both cases. with slug or jobSlug
 * @param locale - the current app locale.
 * @param jobFolderId - the current app locale.
 * @param jobTitle - the current app locale.
 * @param jobSlug - the current app locale.
 * @param slug - the current app locale.
 * @param userId - the current app locale.
 */
export const useUnblockOperations = ({
  userIdToUnlock, rid, locale, jobFolderId, jobTitle, jobSlug, slug, userId,
}: IProps) => {
  // ************************************
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUnlockOptions, setPendingUnlockOptions] = useState<UnlockOptions | null>(null);
  const { status606Toast } = useStaticToasts();

  const handleApiError = useCallback((code: number) => {
    switch (code) {
      case 601:
        showUpgradeSubscriptionModal();
        break;
      case 602:
        showAccountFirstPaymentModal();
        break;
      case 603:
        showAccountSuspendedModal();
        break;
      case 701:
      case 403:
        showAddCompanyInfoModal(locale as Locale, () => {});
        break;
      case 606:
        status606Toast();
        break;
      default:
        bjToast(`Error code: ${code}`, { className: 'border-l-3 border-error' });
    }
  }, [locale, status606Toast]);

  const unblockCv = useCallback(async ({ type, onSuccess }: UnlockOptions) => {
    if (!userIdToUnlock) return;

    try {
      const res = await fetcher(
        `/v1/resume-operation/${userIdToUnlock}/check-unlock/${type}${rid ? `?rid=${rid}` : ''}`,
        locale,
        {
          method: 'POST',
          payload: { jobId: jobFolderId, title: jobTitle, jobSlug },
          stringifyPayload: true,
          withResponseHeaders: true,
        },
      );

      const { status, response } = res as ResponseWithHeaders<ApiCheckUnlock>;

      if (status === 402) {
        const { response: payResponse } = res as ResponseWithHeaders<ApiCheckUnlockPay>;
        if (isCheckUnlockStatusPayOk(payResponse)) {
          showCvContactModal(payResponse.id, locale as Locale);
        }
        return;
      }

      if (isCheckUnlockStatusOk(response)) {
        if (onSuccess) {
          trackEvent({
            eventType: 'action',
            entityType: 'resume',
            entityId: slug,
            action: 'contact',
            viewContext: 'detail',
            ...(userId && { userId: userId.toString() }),
            ...(rid && { listUuid: rid }),
            metadata: [
              { key: 'type', value: type },
            ],
          });
          onSuccess(response);
        }

        return;
      }

      if (isApiV1Errors(response)) {
        handleApiError(response.errors[0].code as number);
      }
    } catch (err) {
      console.error(err);
    }
  }, [userIdToUnlock, rid, locale, jobFolderId, jobTitle, jobSlug, handleApiError, slug, userId]);

  // ----------------------------
  // Request unlock with confirmation modal
  // ----------------------------
  const requestUnlockWithConfirm = useCallback((options: UnlockOptions) => {
    const skipConfirm = localStorage.getItem('skipUnlockConfirm') === 'true';
    if (skipConfirm) {
      void unblockCv(options);
      return;
    }

    setPendingUnlockOptions(options);
    setConfirmModalOpen(true);
  }, [unblockCv]);

  const confirmUnlock = useCallback(() => {
    if (pendingUnlockOptions) {
      void unblockCv(pendingUnlockOptions);
      setConfirmModalOpen(false);
      setPendingUnlockOptions(null);
    }
  }, [pendingUnlockOptions, unblockCv]);

  const cancelUnlock = useCallback(() => {
    setConfirmModalOpen(false);
    setPendingUnlockOptions(null);
  }, []);

  const hasSkipUnlockConfirm = useCallback(() => localStorage.getItem('skipUnlockConfirm') === 'true', []);

  const requestUnlock = useCallback((options: UnlockOptions) => {
    void unblockCv(options);
  }, [unblockCv]);


  const openPricingInformationModal = useCallback(() => {
    const skipConfirm = localStorage.getItem('skipUnlockConfirm') === 'true';
    if (skipConfirm) {
      return;
    }
    setConfirmModalOpen(true);
  }, []);


  return {
    unblockCv,
    hasSkipUnlockConfirm,
    openPricingInformationModal,
    requestUnlock,
    requestUnlockWithConfirm,
    confirmModalOpen,
    confirmUnlock,
    cancelUnlock,
  };
};

